import { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Toaster } from 'react-hot-toast';
import { useAuthStore } from './store/auth-store';
import { MainLayout } from './components/layout/main-layout';
import { AuthLayout } from './components/layout/auth-layout';
import { LoginPage } from './pages/login';
import { ForgotPasswordPage } from './pages/forgot-password';
import { ResetPasswordPage } from './pages/reset-password';
import { DashboardPage } from './pages/dashboard';
import { ClientsPage } from './pages/clients';
import { SalesPage } from './pages/sales';
import { NewSalePage } from './pages/new-sale';
import { ClientDetailPage } from './pages/client-detail';
import { ProfilePage } from './pages/profile';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  const { loadProfile, isAuthenticated } = useAuthStore();
  const currentPath = window.location.pathname;
  const isRecoveryRoute =
    currentPath === '/forgot-password' ||
    currentPath === '/reset-password' ||
    currentPath.startsWith('/reset-password/');
  const [isBootstrapping, setIsBootstrapping] = useState(true);

  useEffect(() => {
    if (isRecoveryRoute) {
      setIsBootstrapping(false);
      return;
    }

    let isMounted = true;

    const bootstrap = async () => {
      try {
        await Promise.race([
          loadProfile(),
          new Promise((resolve) => setTimeout(resolve, 5000)),
        ]);
      } finally {
        if (isMounted) {
          setIsBootstrapping(false);
        }
      }
    };

    bootstrap();

    return () => {
      isMounted = false;
    };
  }, [isRecoveryRoute, loadProfile]);

  if (isBootstrapping) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-purple-50 dark:from-gray-900 dark:to-gray-800">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-300 animate-pulse">Cargando Sales Tracker...</p>
        </div>
      </div>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={
              isAuthenticated ? <Navigate to="/dashboard" replace /> : <AuthLayout><LoginPage /></AuthLayout>
            } />
            <Route path="/forgot-password" element={
              <AuthLayout><ForgotPasswordPage /></AuthLayout>
            } />
            <Route path="/reset-password" element={
              <AuthLayout><ResetPasswordPage /></AuthLayout>
            } />
            <Route path="/reset-password/:token" element={
              <AuthLayout><ResetPasswordPage /></AuthLayout>
            } />
            
            {/* Protected routes */}
            <Route path="/" element={
              isAuthenticated ? <MainLayout /> : <Navigate to="/login" replace />
            }>
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="dashboard" element={<DashboardPage />} />
              <Route path="clients" element={<ClientsPage />} />
              <Route path="clients/:id" element={<ClientDetailPage />} />
              <Route path="sales" element={<SalesPage />} />
              <Route path="sales/new" element={<NewSalePage />} />
              <Route path="profile" element={<ProfilePage />} />
            </Route>
            
            {/* Catch all */}
            <Route path="*" element={<Navigate to={isAuthenticated ? "/" : "/login"} replace />} />
          </Routes>
        </div>
        
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: 'hsl(var(--background))',
              color: 'hsl(var(--foreground))',
              border: '1px solid hsl(var(--border))',
              borderRadius: 'var(--radius)',
              padding: '12px 16px',
            },
            success: {
              iconTheme: {
                primary: 'hsl(var(--primary))',
                secondary: 'hsl(var(--primary-foreground))',
              },
            },
            error: {
              iconTheme: {
                primary: 'hsl(var(--destructive))',
                secondary: 'hsl(var(--destructive-foreground))',
              },
            },
          }}
        />
      </Router>
      
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
}

export default App;
